import React, { useState } from "react"
import { Check, ChevronsUpDown, Plus } from "lucide-react"
import { cn } from "@/lib/utils/cn"
import { Button } from "@/ui-components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/ui-components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui-components/ui/popover"
import { Badge } from "@/ui-components/ui/badge"

export interface CategorySelectorProps {
  categories: string[]
  selected: string[]
  onChange: (categories: string[]) => void
  isLoading?: boolean
  mode?: 'single' | 'multiple'
  allowCreate?: boolean
  placeholder?: string
  className?: string
  onSearchChange?: (value: string) => void
}

export function CategorySelector({
  categories = [],
  selected = [],
  onChange,
  isLoading = false,
  mode = 'single',
  allowCreate = false,
  placeholder = "Kategorie wählen...",
  className,
  onSearchChange
}: CategorySelectorProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")

  const handleSelect = (category: string) => {
    if (mode === 'single') {
      if (selected.includes(category)) {
        onChange([]) // Deselect if already selected (optional, maybe standard is to keep it?)
        // Actually for single select, usually clicking selected doesn't toggle off unless it's clearable.
        // Let's assume standard select behavior: clicking another replaces. Clicking same keeps or toggles?
        // User said "simplify". Standard Select doesn't toggle off. But Command/Combobox often does.
        // Let's toggle for now.
        onChange([])
      } else {
        onChange([category])
      }
      setOpen(false)
    } else {
      // Multiple
      if (selected.includes(category)) {
        onChange(selected.filter((c) => c !== category))
      } else {
        onChange([...selected, category])
      }
    }
  }

  const handleCreate = () => {
    if (!search.trim()) return
    const newCategory = search.trim()
    
    // Check if already exists
    if (categories.includes(newCategory)) {
      handleSelect(newCategory)
      return
    }

    if (mode === 'single') {
      onChange([newCategory])
      setOpen(false)
    } else {
      onChange([...selected, newCategory])
    }
    setSearch("")
  }

  const filteredCategories = categories.filter(category => 
    category.toLowerCase().includes(search.toLowerCase())
  )
  
  // Logic to show "Create ..."
  // Show if search is not empty AND (search is not in categories OR allowCreate is true)
  // Actually simpler: if allowCreate and search is not empty and search is not exactly one of the categories.
  const showCreate = allowCreate && search.trim().length > 0 && !categories.some(c => c.toLowerCase() === search.trim().toLowerCase())

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between h-11 px-3 text-left font-normal", !selected.length && "text-muted-foreground", className)}
          disabled={isLoading}
        >
          <div className="flex gap-1 flex-wrap truncate">
            {selected.length === 0 ? (
              <span className="text-gray-500">{placeholder}</span>
            ) : mode === 'single' ? (
              <span className="truncate">{selected[0]}</span>
            ) : (
              <div className="flex gap-1 flex-wrap">
                {selected.length > 2 ? (
                   <>
                    <Badge variant="secondary" className="mr-1">{selected[0]}</Badge>
                    <span className="text-xs text-muted-foreground self-center">+{selected.length - 1}</span>
                   </>
                ) : (
                  selected.map((cat) => (
                    <Badge key={cat} variant="secondary" className="mr-1">
                      {cat}
                    </Badge>
                  ))
                )}
              </div>
            )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={false}> 
          <CommandInput 
            placeholder="Kategorie suchen..." 
            value={search}
            onValueChange={(val) => {
              setSearch(val)
              onSearchChange?.(val)
            }}
          />
          <CommandList>
            {!showCreate && filteredCategories.length === 0 && (
              <CommandEmpty>Keine Kategorie gefunden.</CommandEmpty>
            )}
            <CommandGroup>
              {filteredCategories.map((category) => (
                <CommandItem
                  key={category}
                  value={category}
                  onSelect={() => handleSelect(category)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      selected.includes(category) ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {category}
                </CommandItem>
              ))}
            </CommandGroup>
            {showCreate && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    key={`create-${search}`}
                    value={`create-${search}`}
                    onSelect={handleCreate}
                    className="text-blue-600 cursor-pointer"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    "{search}" erstellen
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
